/* ============================================================
   SECTIONS MANAGER — rename, reorder, and show/hide sections.
   Writes the editable registry to siteSections/{locale}. The section
   `key` is immutable (it maps to a bespoke component + content); only
   the display label, order, and visibility are editable here. Numbers
   and the rail's uppercase names are derived from order automatically.
   Hero is pinned first and always visible.
   ============================================================ */
import { useEffect, useState } from 'react'
import { useSections } from '../content/ContentContext.jsx'
import { DEFAULT_LOCALE } from '../content/site.js'
import { saveSiteSections } from '../content/loader.js'
import { Icon } from '../components/Icon.jsx'

export function SectionsManager() {
  const live = useSections()
  // Local working copy so reorder/rename feel instant; saved on demand.
  const [items, setItems] = useState(live)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Re-sync from live when not mid-edit (e.g. first load / external change).
  useEffect(() => {
    setItems(live)
  }, [live])

  // Hero is pinned first; only the rest can be reordered/hidden.
  const movable = items.filter((s) => s.key !== 'hero')

  const renumber = (list) => {
    const hero = items.find((s) => s.key === 'hero')
    const next = hero ? [hero, ...list] : list
    return next.map((s, i) => ({ ...s, num: String(i).padStart(2, '0') }))
  }

  const rename = (key, label) =>
    setItems((arr) => arr.map((s) => (s.key === key ? { ...s, label } : s)))

  const toggleHidden = (key) =>
    setItems((arr) => arr.map((s) => (s.key === key ? { ...s, hidden: !s.hidden } : s)))

  const move = (key, dir) => {
    const i = movable.findIndex((s) => s.key === key)
    const j = i + dir
    if (i < 0 || j < 0 || j >= movable.length) return
    const reordered = movable.slice()
    ;[reordered[i], reordered[j]] = [reordered[j], reordered[i]]
    setItems(renumber(reordered))
  }

  async function save() {
    setSaving(true)
    setSaved(false)
    try {
      await saveSiteSections(DEFAULT_LOCALE, items)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      console.warn('[sections] save failed.', e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="sections-mgr">
      <p className="picker-hint">
        <Icon name="layers" size={18} />
        <span>
          Rename, reorder, and show/hide sections. The Hero is always first. Section content is
          edited from the Dashboard.
        </span>
      </p>

      <div className="panel">
        {items
          .filter((s) => s.key === 'hero')
          .map((s) => (
            <div className="sec-row" key={s.key}>
              <div className="sr-num">{s.num}</div>
              <input
                className="sec-name-input"
                value={s.label}
                onChange={(e) => rename(s.key, e.target.value)}
              />
              <span className="sr-meta">pinned</span>
            </div>
          ))}

        {movable.map((s, i) => (
          <div className={'sec-row sec-row-edit' + (s.hidden ? ' is-hidden' : '')} key={s.key}>
            <div className="sr-num">{s.num}</div>
            <input
              className="sec-name-input"
              value={s.label}
              onChange={(e) => rename(s.key, e.target.value)}
            />
            <div className="sr-actions">
              <button
                className={'sr-icon-btn' + (s.hidden ? ' off' : '')}
                aria-label={s.hidden ? 'Hidden — show this section' : 'Visible — hide this section'}
                title={s.hidden ? 'Hidden — show' : 'Visible — hide'}
                onClick={() => toggleHidden(s.key)}
              >
                <Icon name={s.hidden ? 'circle' : 'eye'} size={16} />
              </button>
              <button
                className="sr-icon-btn"
                disabled={i === 0}
                onClick={() => move(s.key, -1)}
                aria-label="Move up"
                title="Move up"
              >
                <Icon name="arrowUp" size={16} />
              </button>
              <button
                className="sr-icon-btn"
                disabled={i === movable.length - 1}
                onClick={() => move(s.key, 1)}
                aria-label="Move down"
                title="Move down"
              >
                <Icon name="arrowUp" size={16} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="sections-actions">
        {saved && <span className="saved-note">Saved ✓</span>}
        <button className="btn-pub" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}
